import React, { memo } from 'react';
import { cn } from '@/lib/utils';

interface SkeletonProps {
  className?: string;
  count?: number;
  height?: number | string;
  width?: number | string;
}

export const Skeleton = memo<SkeletonProps>(({ 
  className = '', 
  count = 1, 
  height = 20, 
  width = '100%' 
}) => {
  const skeletons = Array.from({ length: count }, (_, index) => (
    <div
      key={index}
      className={cn(
        'animate-pulse bg-muted rounded',
        className
      )}
      style={{ 
        height: typeof height === 'number' ? `${height}px` : height,
        width: typeof width === 'number' ? `${width}px` : width
      }}
    />
  ));

  return count === 1 ? skeletons[0] : <>{skeletons}</>;
});

Skeleton.displayName = 'Skeleton';

// Pre-built skeleton components for common use cases
export const TableSkeleton = memo<{ rows?: number; columns?: number }>(({ 
  rows = 5, 
  columns = 4 
}) => (
  <div className="space-y-3">
    {/* Header skeleton */}
    <div className="flex space-x-4">
      {Array.from({ length: columns }, (_, i) => (
        <Skeleton key={i} height={20} className="flex-1" />
      ))}
    </div>
    
    {/* Row skeletons */}
    {Array.from({ length: rows }, (_, rowIndex) => (
      <div key={rowIndex} className="flex space-x-4">
        {Array.from({ length: columns }, (_, colIndex) => (
          <Skeleton key={colIndex} height={16} className="flex-1" />
        ))}
      </div>
    ))}
  </div>
));

TableSkeleton.displayName = 'TableSkeleton';

export const CardSkeleton = memo(() => (
  <div className="border rounded-lg p-4 space-y-3">
    <Skeleton height={200} className="rounded" />
    <Skeleton height={24} width="70%" />
    <Skeleton height={16} width="100%" count={2} className="space-y-2" />
    <div className="flex space-x-2 pt-2">
      <Skeleton height={32} width={80} className="rounded-full" />
      <Skeleton height={32} width={80} className="rounded-full" />
    </div>
  </div>
));

CardSkeleton.displayName = 'CardSkeleton';

export const ListSkeleton = memo<{ items?: number }>(({ items = 3 }) => (
  <div className="space-y-4">
    {Array.from({ length: items }, (_, index) => (
      <div key={index} className="flex items-center space-x-4">
        <Skeleton height={48} width={48} className="rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton height={16} width="60%" />
          <Skeleton height={14} width="40%" />
        </div>
      </div>
    ))}
  </div>
));

ListSkeleton.displayName = 'ListSkeleton';